import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { HEALTH_STATE } from "contracts/enums/core";
import { pathSegments } from "../../boundaries/url-path.parser";
import type { PageMapLens } from "../../enums/map";
import { PAGE_MAP_LENS } from "../../enums/map";
import { isOpen } from "../website/issueModel";
import { clamp, clampView } from "./estateFormat";
import {
  type EstateEdge,
  type EstateLayout,
  type EstateNode,
  FULL_FIT_MAX_ZOOM,
  type Page,
  type ViewTransform,
} from "./estateTypes";
import { issueLinkMatchesPage } from "./issuePageResolution";
import { pageContentFitnessScore, pageHealthClass } from "./scanModel";

export function linkedIssueCount(data: OverviewResponse, page: Page): number {
  return data.issues.filter(
    (issue) =>
      isOpen(issue) &&
      issue.pages.some((link) => issueLinkMatchesPage(link, page)),
  ).length;
}

export function edgeRank(
  edge: EstateEdge,
  contextPageId: string | null,
  lens: PageMapLens,
): number {
  let rank = 0;
  if (contextPageId && touches(edge, contextPageId)) {
    rank += 120;
  }
  if (edge.link.agentBroken) {
    rank += 90;
  }
  if (edge.from.important || edge.to.important) {
    rank += 34;
  }
  if (pageHealthClass(scoreFor(edge.from.page, lens)) === HEALTH_STATE.POOR) {
    rank += 22;
  }
  if (pageHealthClass(scoreFor(edge.to.page, lens)) === HEALTH_STATE.POOR) {
    rank += 22;
  }
  if (edge.from.page.path === "/" || edge.to.page.path === "/") {
    rank += 18;
  }
  if (edge.from.cluster !== edge.to.cluster) {
    rank += 9;
  }
  rank += structuralRelationshipRank(
    edge.from.page.path,
    edge.to.page.path,
    edge.from.cluster === edge.to.cluster,
  );
  return rank;
}

export function touches(edge: EstateEdge, pageId: string): boolean {
  return edge.from.page.id === pageId || edge.to.page.id === pageId;
}

export function scoreFor(page: Page, lens: PageMapLens): number | null {
  return lens === PAGE_MAP_LENS.AGENT
    ? page.agentScore
    : pageContentFitnessScore(page);
}

export function scoreLabel(
  score: number | null,
  lens: PageMapLens = PAGE_MAP_LENS.HUMAN,
): string {
  if (score !== null) {
    return `${score}`;
  }
  return lens === PAGE_MAP_LENS.AGENT ? "Not available" : "Not scored";
}

export function stateLabel(
  state: string,
  lens: PageMapLens = PAGE_MAP_LENS.HUMAN,
): string {
  switch (state) {
    case "good":
      return "Good";
    case "watch":
      return "Watch";
    case "poor":
      return "Needs attention";
    default:
      return lens === PAGE_MAP_LENS.AGENT ? "Not available" : "Not scored";
  }
}

export function lensLabel(lens: PageMapLens): string {
  return lens === PAGE_MAP_LENS.AGENT ? "Agent diagnostic" : "Content Fitness";
}

export function fitTransform(
  layout: EstateLayout,
  compact: boolean,
  focusNode: EstateNode | null,
): ViewTransform {
  const padding = compact ? 3 : 4;
  const bounds = contentBounds(layout);
  const contentWidth = Math.max(bounds.right - bounds.left, 1);
  const contentHeight = Math.max(bounds.bottom - bounds.top, 1);
  const scale = Math.min(
    compact ? 1.04 : FULL_FIT_MAX_ZOOM,
    (layout.viewport.width - padding * 2) / contentWidth,
    (layout.viewport.height - padding * 2) / contentHeight,
  );
  const x = focusNode
    ? layout.viewport.width * 0.42 - focusNode.x * scale
    : (layout.viewport.width - contentWidth * scale) / 2 - bounds.left * scale;
  const y =
    (layout.viewport.height - contentHeight * scale) / 2 - bounds.top * scale;
  return clampView({ x, y, scale }, layout);
}

function contentBounds(layout: EstateLayout): {
  left: number;
  top: number;
  right: number;
  bottom: number;
} {
  if (layout.bands.length === 0 && layout.nodes.length === 0) {
    return { left: 0, top: 0, right: layout.width, bottom: layout.height };
  }
  const bands = layout.bands.reduce(
    (bounds, band) => ({
      left: Math.min(bounds.left, band.x),
      top: Math.min(bounds.top, band.y),
      right: Math.max(bounds.right, band.x + band.width),
      bottom: Math.max(bounds.bottom, band.y + band.height),
    }),
    {
      left: Number.POSITIVE_INFINITY,
      top: Number.POSITIVE_INFINITY,
      right: Number.NEGATIVE_INFINITY,
      bottom: Number.NEGATIVE_INFINITY,
    },
  );
  return layout.nodes.reduce(
    (bounds, node) => ({
      left: Math.min(bounds.left, node.x - node.width / 2),
      top: Math.min(bounds.top, node.y - node.height / 2),
      right: Math.max(bounds.right, node.x + node.width / 2),
      bottom: Math.max(bounds.bottom, node.y + node.height / 2),
    }),
    bands,
  );
}

function structuralRelationshipRank(
  fromPath: string,
  toPath: string,
  sameCluster: boolean,
): number {
  const from = pathSegments(fromPath);
  const to = pathSegments(toPath);
  if (from.length === 0 && to.length === 1) {
    return 90;
  }
  if (to.length === 0 && from.length === 1) {
    return 90;
  }
  const shorter = from.length <= to.length ? from : to;
  const longer = from.length <= to.length ? to : from;
  const directParent =
    longer.length === shorter.length + 1 &&
    shorter.every((segment, index) => longer[index] === segment);
  if (directParent) {
    return 80;
  }
  return sameCluster ? 24 : 0;
}

export function zoomTransform(
  current: ViewTransform,
  viewportX: number,
  viewportY: number,
  factor: number,
  minScale: number,
  maxScale: number,
): ViewTransform {
  const nextScale = clamp(current.scale * factor, minScale, maxScale);
  const contentX = (viewportX - current.x) / current.scale;
  const contentY = (viewportY - current.y) / current.scale;
  return {
    x: viewportX - contentX * nextScale,
    y: viewportY - contentY * nextScale,
    scale: nextScale,
  };
}
